import { useAppSelector } from 'state/hooks'
import {
  closeForm,
  selectBuiltFormTabs,
  selectFormTab,
  selectFormOpen,
  formAction,
  selectInputVal,
  setInput,
  DEPOSIT_TAB,
  WITHDRAW_TAB,
  BORROW_TAB,
  REPAY_TAB,
  selectTxReviewOpen,
  openTxReview,
  closeTxReview,
  setTxAmount,
  selectTxAmount
} from 'state/slices/ui/form'
import Tabs from 'components/common/Tabs'
import InfoPoint from '../common/InfoPoint'
import ParamList from './ParamList'
import CloseButton from 'components/common/CloseButton'
import { MetaBroker, FormattedPositions } from 'state/mock'
import { isNil } from 'ramda'
import EmptyForm from './EmptyForm'
import {
  detrmineHealthColor,
  formatHealthFactor,
  formatPercentage,
  prettyTokenBal,
  prettyTokenBalFull,
  formatNum,
  scaleUp
} from 'toolbox/format'
import { BORROW, COLLATERAL, selectDashTab } from 'state/slices/ui/dash'
import {
  estimateNewAvailable,
  estimateNewBorrowed,
  calcNewUtil,
  calcLendRate,
  calcUtil,
  floorAndFixToEightDecimals
} from 'toolbox/calc'
import {
  calcBorrowData,
  calcHealthFactor,
  calcSupplyData,
  isRedZone,
  isYellowZone,
  selectHealthFactor,
  selectMaxBorrowForAsset,
  selectPortfolio
} from 'state/slices/user/portfolio'
import { selectPortfolioBusy } from 'state/slices/user/portfolio'
import { useWallet } from '@aptos-labs/wallet-adapter-react'
import {
  getWalletBalanceByName,
  selectLoadedWalletBalances
} from 'state/slices/user/walletBalances'
import { selectUnderlyingNamesFromBrokers } from 'state/slices/app/brokers'
import { SDK_CONTEXT } from 'state/context'
import {
  buildCurrentPortfolioBasicState,
  buildNextPortfolioState,
  getNextPosition,
  getNextWalletBalance,
  shouldGetRiskEval
} from 'state/thunks/doTx'
import { useEffect, useMemo, useState } from 'react'
import { selectTxStatus } from 'state/slices/ui/transaction'
import { ParamProps } from './Param'
import { TxFormReview } from './TxFormReview'
import InfoBox, { InfoBoxProps } from '../common/InfoBox'
import { InputContainer } from './InputContainer'

export interface TxFormProps {
  broker: MetaBroker
  isLoadedUser: boolean
  positions: FormattedPositions
}

function TxForm({ broker, isLoadedUser, positions }: TxFormProps) {
  //state and selectors
  const tabs = useAppSelector(selectBuiltFormTabs)
  const selectedTab = useAppSelector(selectFormTab)
  const dashTab = useAppSelector(selectDashTab)
  const inputVal = useAppSelector(selectInputVal)
  const inputAmount = parseFloat(inputVal)
  const txAmount = useAppSelector(selectTxAmount)
  const formOpen = useAppSelector(selectFormOpen)
  const isUserLoadBusy = useAppSelector(selectPortfolioBusy)
  const healthFactor = useAppSelector(selectHealthFactor)
  const txReviewOpen = useAppSelector(selectTxReviewOpen)
  const balances = useAppSelector(selectLoadedWalletBalances)
  const { account, signAndSubmitTransaction } = useWallet()
  const basicPortfolio = useAppSelector(selectPortfolio)
  const hasInput = parseFloat(inputVal) > 0
  const brokerNames = useAppSelector(selectUnderlyingNamesFromBrokers)
  const isTxPending = useAppSelector(selectTxStatus) === 'pending'
  const parsedInput = parseFloat(inputVal) || 0
  const maxBorrowsForAsset = useAppSelector((s) =>
    selectMaxBorrowForAsset(s, broker?.loanNote?.name)
  )

  const [focusTrigger, setFocusTrigger] = useState(0)

  useEffect(() => {
    if (formOpen && !txReviewOpen) {
      setFocusTrigger((n) => n + 1)
    }
  }, [formOpen, selectedTab, txReviewOpen])

  //risk simulation UX state
  const [isSimulatedLoading, setIsSimulatedLoading] = useState<boolean>(false)
  //save for display purposes only
  const [simHealthFactor, setSimHealthFactor] = useState<number>(0)
  //use to show warning colors in stateful update to avoid flickering
  const [simHealthYellow, setSimHealthYellow] = useState<boolean>(false)
  const [simHealthRed, setSimHealthRed] = useState<boolean>(false)
  //use to differntiate between warning red and unhealthy red messaging
  const [isSimHealthy, setIsSimHealthy] = useState<boolean>(false)
  const [isLTVWarning, setIsLTVWarning] = useState<boolean>(false)
  const [ltv, setLTV] = useState<number>(0)

  //portfolio builders for simulator
  const currentPortfolioBasic = useMemo(
    () => buildCurrentPortfolioBasicState(basicPortfolio),
    [basicPortfolio]
  )

  const nextPortfolio = useMemo(
    () => buildNextPortfolioState(currentPortfolioBasic, selectedTab, txAmount, broker),
    [currentPortfolioBasic, selectedTab, txAmount, broker]
  )

  const nextPosition = useMemo(
    () => getNextPosition(nextPortfolio, selectedTab, broker),
    [nextPortfolio, selectedTab, broker]
  )

  //risk simulation UX with batched state updates
  useEffect(() => {
    const fetchSimRiskData = async () => {
      if (isLoadedUser && hasInput) {
        const shouldGetRisk = shouldGetRiskEval(selectedTab, nextPortfolio)

        if (shouldGetRisk && !blockCalcs) {
          setIsSimulatedLoading(true)

          try {
            const response = await SDK_CONTEXT.superSdk.fetcher.fetchSimulatedPortfolio(
              nextPortfolio
            )
            console.log('SIMULATION: ', response)

            const simFactor = calcHealthFactor(response)
            console.log('LTV: ', response.ltv)
            const simYellow = isYellowZone(simFactor)
            const simRed = isRedZone(simFactor)
            const healthy = simFactor > 1.0
            const ltvWarn = response.ltv > 0.95
            setSimHealthFactor(simFactor)
            setSimHealthYellow(simYellow)
            setSimHealthRed(simRed)
            setIsSimHealthy(healthy)
            setIsLTVWarning(healthy && ltvWarn)
            if (ltvWarn) {
              setLTV(response.ltv)
            }
          } catch (e) {
            setSimHealthFactor(0)
            setIsSimHealthy(false)
            setSimHealthRed(true)
            setIsSimulatedLoading(false)
            setIsLTVWarning(false)
            setLTV(0)
          } finally {
            setIsSimulatedLoading(false)
          }
        } else {
          setIsSimulatedLoading(false)
          setSimHealthYellow(false)
          setSimHealthRed(false)
          setIsLTVWarning(false)
          setLTV(0)
        }
      }
    }

    fetchSimRiskData()
  }, [isLoadedUser, hasInput, basicPortfolio, selectedTab, txAmount, broker, nextPortfolio])

  const resetSimValues = () => {
    setSimHealthYellow(false)
    setSimHealthRed(false)
    setIsSimHealthy(false)
    setIsLTVWarning(false)
    setLTV(0)
  }

  useEffect(() => {
    if (!broker) {
      resetSimValues()
    }
  })

  useEffect(() => {
    if (txReviewOpen) {
      resetSimValues()
    }
  }, [txReviewOpen])

  useEffect(() => {
    if (!hasInput) {
      resetSimValues()
    }
  }, [hasInput])

  if (isNil(broker)) {
    return (
      <div className="tx-form">
        <CloseButton cb={closeForm} />
        <EmptyForm tabs={tabs} selectedTab={selectedTab} />
      </div>
    )
  }
  const token = broker?.tokenMeta
  const chain = broker?.chainMeta
  const price = broker?.underlyingAsset?.price
  const inputDollars = parsedInput > 0 ? parsedInput * price : 0
  const utilization = calcUtil(broker)
  const supplyData = calcSupplyData(positions, broker)
  const borrowData = calcBorrowData(positions, broker)
  const brokerBorrowedUnderlying = broker?.scaledTotalBorrowedUnderlying
  const totalAvailableUnderlying = broker?.scaledAvailableLiquidityUnderlying
  const walletUnderlyingTokenBalance = getWalletBalanceByName(balances, token.name)
  const isBorrowTab = selectedTab === BORROW_TAB

  //next values
  const nextAvail = estimateNewAvailable(broker, inputVal, selectedTab)
  const nextTotalBorrowed = estimateNewBorrowed(brokerBorrowedUnderlying, inputVal, selectedTab)
  const nextTotalSupplied = nextAvail + nextTotalBorrowed
  const nextUtil = calcNewUtil(nextTotalBorrowed, nextTotalSupplied)
  const nextBorrowInterestRate = SDK_CONTEXT.superSdk.getInterestRate(
    nextUtil,
    broker.interestRateCurve
  )
  const stabilityFeeRate = 0.0015 // 0.15%
  const nextBorrowAPR = nextBorrowInterestRate + stabilityFeeRate
  // None of the stability fee is passed to the lender
  const nextSupplyAPY = calcLendRate(nextBorrowAPR, broker.interestFeeRate, nextUtil)
  const brokerTotal = brokerBorrowedUnderlying + totalAvailableUnderlying
  const nextWalletBalance = getNextWalletBalance(
    selectedTab,
    inputAmount,
    walletUnderlyingTokenBalance
  )

  //broker maximums
  const maxSupplyBroker = Number(broker?.maxDepositScaled)
  const maxBorrowBrokerLimit = Number(broker?.maxBorrowScaled)

  //max deposit logic
  const overBrokerDepositLimit = nextTotalSupplied > maxSupplyBroker
  const depositDiffToBrokerLimit = maxSupplyBroker - brokerTotal
  const depositDiffToBrokerLimitNegative = depositDiffToBrokerLimit <= 0
  const poolIsFull = selectedTab === DEPOSIT_TAB && depositDiffToBrokerLimitNegative
  const walletBalanceOrDepositDiffLess =
    walletUnderlyingTokenBalance < depositDiffToBrokerLimit
      ? walletUnderlyingTokenBalance
      : depositDiffToBrokerLimit
  const walletBalanceOrDepositDiffLessShaved =
    walletUnderlyingTokenBalance < depositDiffToBrokerLimit
      ? floorAndFixToEightDecimals(walletUnderlyingTokenBalance)
      : depositDiffToBrokerLimit

  //max withdraw logic
  const suppOrAvailUnderlyingLower = supplyData.underlyingTokenBalance < totalAvailableUnderlying
  const maxWithdrawUnderlyingUser = Math.min(
    supplyData.underlyingTokenBalance,
    totalAvailableUnderlying
  )
  const maxWithdrawUnderlyingUserShaved = floorAndFixToEightDecimals(maxWithdrawUnderlyingUser)

  let maxWithdrawNoteUser = 0
  if (broker?.depositNote) {
    const currentPortfolioPosition = currentPortfolioBasic.collaterals.find(
      (c) => c.instrumentId === broker?.depositNote.name
    )
    if (currentPortfolioPosition) {
      maxWithdrawNoteUser = parseInt(currentPortfolioPosition.amount)
    }
  }

  //max repay logic
  const maxRepayUnderlyingTokenBalance = borrowData.underlyingTokenBalance
  const maxRepayPortLower = maxRepayUnderlyingTokenBalance < walletUnderlyingTokenBalance
  const maxRepayPortOrWalletBalance = maxRepayPortLower
    ? maxRepayUnderlyingTokenBalance
    : walletUnderlyingTokenBalance

  let maxRepayNoteUser = 0
  if (broker?.loanNote) {
    const currentPortfolioPosition = currentPortfolioBasic.liabilities.find(
      (c) => c.instrumentId === broker?.loanNote.name
    )
    if (currentPortfolioPosition) {
      maxRepayNoteUser = parseInt(currentPortfolioPosition.amount)
    }
  }

  //broker limit max borrow logic
  const overBrokerBorrowLimit = nextTotalBorrowed > maxBorrowBrokerLimit
  const borrowDiffToBrokerLimit = maxBorrowBrokerLimit - brokerBorrowedUnderlying
  const borrowDiffToBrokerLimitNegative = borrowDiffToBrokerLimit <= 0
  const borrowsAreFull = selectedTab === BORROW_TAB && borrowDiffToBrokerLimitNegative
  const totalAvailableLessThanBrokerLimit = totalAvailableUnderlying < borrowDiffToBrokerLimit
  const borrowAvailOrLimit = totalAvailableLessThanBrokerLimit
    ? totalAvailableUnderlying
    : borrowDiffToBrokerLimit
  const { maxBorrowUnderlying } = maxBorrowsForAsset

  const nextBorrowExceedsAvailable = nextTotalBorrowed > totalAvailableUnderlying

  const maxTxValueUnderlying = function setMaxValue() {
    switch (selectedTab) {
      case DEPOSIT_TAB:
        return walletBalanceOrDepositDiffLess > 0 ? walletBalanceOrDepositDiffLess : 0
      case WITHDRAW_TAB:
        return maxWithdrawUnderlyingUser
      case BORROW_TAB:
        return borrowAvailOrLimit
      case REPAY_TAB:
        return maxRepayPortOrWalletBalance
    }
  }

  //max value of token to transact logic
  const maxTxValueUnderlyingShaved = function setMaxValue() {
    switch (selectedTab) {
      case DEPOSIT_TAB:
        return walletBalanceOrDepositDiffLessShaved > 0 ? walletBalanceOrDepositDiffLessShaved : 0
      case WITHDRAW_TAB:
        return maxWithdrawUnderlyingUserShaved
      case BORROW_TAB:
        return floorAndFixToEightDecimals(borrowAvailOrLimit)
      case REPAY_TAB:
        return floorAndFixToEightDecimals(maxRepayPortOrWalletBalance)
    }
  }

  //calc if over a limit
  const over = inputAmount > maxTxValueUnderlying()

  //add extra logic to differentiate unhealthy from over limits but still disable
  const overLimit = () => {
    let isOver = false
    switch (selectedTab) {
      case DEPOSIT_TAB:
        isOver = over || poolIsFull || overBrokerDepositLimit
        break
      case WITHDRAW_TAB:
        isOver = over || simHealthRed || isLTVWarning
        break
      case BORROW_TAB:
        isOver = over || simHealthRed || borrowsAreFull || overBrokerBorrowLimit || isLTVWarning
        break
      case REPAY_TAB:
        isOver = over
        break
    }
    return isOver
  }

  //risk coloration classes
  const isYellowZoneStyles =
    simHealthYellow && !over && !isLTVWarning && hasInput ? 'yellow-zone' : ''
  const isRedZoneStyles = simHealthRed && !over && !isLTVWarning && hasInput ? 'red-zone' : ''
  const isGreenZoneStyles = !simHealthRed && !simHealthYellow && !over && hasInput
  const currentHealthYellow = isYellowZone(healthFactor)
  const currentHealthRed = isRedZone(healthFactor)
  const currentHealthGreen = !currentHealthRed && !currentHealthYellow && !over

  //over lmit messaging logic
  function overLimitMessage() {
    switch (selectedTab) {
      case DEPOSIT_TAB:
        return poolIsFull
          ? 'Pool is full'
          : overBrokerDepositLimit
          ? `Exceeds ${token.ticker} deposit limit`
          : 'Exceeds wallet balance'
      case WITHDRAW_TAB:
        return over
          ? suppOrAvailUnderlyingLower
            ? 'Exceeds supplied balance'
            : 'Exceeds available liquidity'
          : !isSimHealthy
          ? 'User would become unhealthy'
          : 'Nearing liquidation threshold'
      case BORROW_TAB:
        return borrowsAreFull
          ? 'Broker borrow limit reached'
          : overBrokerBorrowLimit
          ? `Exceeds ${token.ticker} borrow limit`
          : nextBorrowExceedsAvailable
          ? 'Exceeds available liquidity'
          : simHealthRed
          ? !isSimHealthy
            ? 'User would become unhealthy'
            : 'Nearing liquidation threshold'
          : 'Exceeds max safe borrow'

      case REPAY_TAB:
        return maxRepayPortLower ? 'Exceeds portfolio debt' : 'Exceeds wallet balance'
    }
  }

  function overLimitInfoBox() {
    const ticker = token.ticker
    switch (selectedTab) {
      case DEPOSIT_TAB:
        if (poolIsFull)
          return `${ticker} pool is full. Pool limits are set by the broker and can be adjusted. They are in place to protect the health of the pool and the safety of the users.`
        if (overBrokerDepositLimit)
          return `Amount exceeds max deposit value set for ${ticker} by broker`
        return `Amount exceeds wallet balance of ${ticker}`
      case WITHDRAW_TAB:
        return suppOrAvailUnderlyingLower
          ? `Amount exceeds supplied balance of ${ticker}`
          : `Amount exceeds available liquidity for ${ticker} in broker`
      case BORROW_TAB:
        if (borrowsAreFull)
          return `Borrowing for this broker has reached a maximum limit set by the broker.  This limit can be adjusted, and is in place to protect the health of the pool and the safety of the users.`
        if (overBrokerBorrowLimit)
          return `Amount exceeds max borrow value set for ${ticker} by broker`
        if (nextBorrowExceedsAvailable)
          return `Amount exceeds available liquidity for ${ticker} in broker`
        return ''
      case REPAY_TAB:
        return maxRepayPortLower
          ? `Amount exceeds portfolio ${ticker} debt balance to repay`
          : `Amount exceeds ${ticker} wallet balance`
      default:
        return ''
    }
  }

  const infoBoxText = overLimit()
    ? isLTVWarning
      ? `Amount results in ${formatPercentage(ltv)} LTV, exceeding 95% max LTV limit`
      : overLimitInfoBox()
    : null
  const blockCalcs = nextPosition < 0 || nextWalletBalance < 0 || over

  const maxParam =
    selectedTab === BORROW_TAB || selectedTab === REPAY_TAB
      ? null
      : {
          label: 'Pool max limit',
          value: prettyTokenBal(maxSupplyBroker),
          tooltip: 'Maximum liquidity that can be supplied to the pool'
        }

  //broker params and projections
  const brokerStats = [
    {
      label: 'Total Available In Broker',
      value: prettyTokenBal(totalAvailableUnderlying),
      tooltip: `Liquidity available for borrowing or withdrawal in tokens`,
      next: !blockCalcs && hasInput ? prettyTokenBal(nextAvail) : null
    },
    {
      label: 'Total Loaned By Broker',
      value: prettyTokenBal(brokerBorrowedUnderlying),
      tooltip: `Liquidity currently loaned to borrowers in tokens`,
      next: !blockCalcs && hasInput ? prettyTokenBal(nextTotalBorrowed) : null
    },
    {
      label: 'Total Supplied In Broker',
      value: prettyTokenBal(brokerTotal),
      tooltip: 'Liquidity supplied in tokens',
      next: !blockCalcs && hasInput ? prettyTokenBal(nextTotalSupplied) : null
    },
    maxParam,
    {
      label: 'Utilization',
      value: formatPercentage(utilization),
      tooltip: 'Ratio of debt / collateral.  High utilization increases interest',
      next: !blockCalcs && hasInput ? formatPercentage(nextUtil) : null
    }
  ]

  //health factor display logic
  const healthDisplay = formatHealthFactor(healthFactor)
  const simHealthDisplay = formatHealthFactor(simHealthFactor)

  //param builders
  const healthFactorParam = {
    label: 'Health factor',
    value: !isLoadedUser && isUserLoadBusy ? '--' : healthDisplay,
    tooltip: 'Health simulation of next position required to be 1.2x or higher for user safety',
    next: blockCalcs || !hasInput ? null : isSimulatedLoading ? '--' : simHealthDisplay,
    currentColor: detrmineHealthColor(currentHealthGreen, currentHealthRed, currentHealthYellow),
    simColor: detrmineHealthColor(isGreenZoneStyles, simHealthRed, simHealthYellow)
  }

  const userSuppliedParam = {
    label: 'Supplied',
    value: prettyTokenBalFull(supplyData.underlyingTokenBalance) + ' ' + token?.ticker,
    next: !blockCalcs && hasInput ? prettyTokenBalFull(nextPosition) + ' ' + token?.ticker : null,
    tooltip: 'Current collateral balance in tokens.  Max amount user can withdraw unless unhealthy'
  }

  const supplyAPYParam = {
    label: 'Supply APY',
    value: formatPercentage(supplyData.supplyAPR),
    tooltip: 'Rate of earning interest on deposits over one year',
    next: !blockCalcs && hasInput && nextSupplyAPY < 100 ? formatPercentage(nextSupplyAPY) : null
  }

  const userBorrowedParam = {
    label: 'Borrowed',
    value: prettyTokenBalFull(borrowData.underlyingTokenBalance) + ' ' + token?.ticker,
    next: !blockCalcs && hasInput ? prettyTokenBalFull(nextPosition) + ' ' + token?.ticker : null,
    tooltip: 'Current debt balance in tokens.  Max amount user can repay'
  }

  const borrowAPRParam = {
    label: 'Borrow APR',
    value: formatPercentage(borrowData.borrowAPR),
    tooltip: 'High utilization = more demand, increasing borrower interest',
    next: !blockCalcs && hasInput ? formatPercentage(nextBorrowAPR) : null
  }

  const walletBalanceParam = {
    label: 'Wallet balance',
    value: `${prettyTokenBalFull(walletUnderlyingTokenBalance)} ${token?.ticker}`,
    next:
      !blockCalcs && hasInput && nextWalletBalance > 0
        ? `${prettyTokenBalFull(nextWalletBalance)} ${token?.ticker}`
        : null
  }

  //params for modals
  const depositParams = [healthFactorParam, userSuppliedParam, supplyAPYParam]
  const borrowParams = [healthFactorParam, userBorrowedParam, borrowAPRParam]
  const belowParams = [walletBalanceParam]

  function formParams(): ParamProps[] {
    switch (dashTab) {
      case COLLATERAL:
        return depositParams
      case BORROW:
        return borrowParams
    }
  }

  //form actions and button functions
  const action = () => {
    if (txReviewOpen) {
      formAction(
        selectedTab,
        broker,
        txAmount,
        inputAmount,
        account?.address as string,
        positions,
        signAndSubmitTransaction,
        brokerNames
      )
    } else {
      openTxReview()
    }
  }

  const buttonText = isSimulatedLoading
    ? 'Simulating health factor'
    : overLimit()
    ? isLTVWarning
      ? 'LTV would exceed 95%'
      : overLimitMessage()
    : txReviewOpen
    ? `${selectedTab} ${token?.ticker}`
    : 'Review'

  //final safety check
  const zeroInput = inputAmount <= 0
  const disabledForm = !isLoadedUser || overLimit() || zeroInput || isSimulatedLoading
  const disabledReview = disabledForm || isTxPending
  const txReviewProps = {
    closeForm,
    selectedTab,
    account,
    inputVal,
    token,
    formParams,
    moreParams: brokerStats,
    closeTxReview,
    action,
    disabled: disabledReview,
    buttonText
  }

  //short circuit to TX review (step 2)
  if (txReviewOpen) {
    return <TxFormReview {...txReviewProps} />
  }

  const blueInfoBoxProps: InfoBoxProps = {
    color: 'yellow',
    showBox: (over && infoBoxText) || isLTVWarning ? true : false,
    text: infoBoxText,
    fullWidth: true
  }

  const orangeInfoBoxProps: InfoBoxProps = {
    color: 'orange',
    showBox: isYellowZoneStyles ? true : false,
    text: 'Your position is nearing the liquidation threshold.',
    fullWidth: true
  }

  const redInfoBoxProps: InfoBoxProps = {
    color: 'red',
    showBox: isRedZoneStyles ? true : false,
    text: !isSimHealthy
      ? 'Your portfolio would become unhealthy.'
      : 'Your position is nearing the liquidation threshold.',
    fullWidth: true
  }

  const actionButtonProps = {
    className: 'sp-btn primary large full-w send-btn',
    onClick: action,
    disabled: disabledForm
  }

  function setMax() {
    switch (selectedTab) {
      case BORROW_TAB:
        {
          const maxUnderlying = maxTxValueUnderlyingShaved()
          const maxBorrowLessAvailable = maxBorrowUnderlying < maxUnderlying
          if (maxBorrowLessAvailable) {
            setInputAndTxAmount(floorAndFixToEightDecimals(maxBorrowUnderlying).toString())
          } else {
            setInputAndTxAmount(maxUnderlying.toString())
          }
        }
        break
      case REPAY_TAB: {
        const roundedMax = floorAndFixToEightDecimals(maxTxValueUnderlying())
        const inputString = roundedMax.toString()
        if (walletUnderlyingTokenBalance > maxRepayUnderlyingTokenBalance) {
          // the wallet has enough to fully repay the loan
          // so we can use the total loan amount of notes to repay
          setInput(inputString)
          setTxAmount(maxRepayNoteUser)
        } else {
          // the wallet does not have enough to fully repay the loan
          // so we can use the wallet balance to repay
          setInputAndTxAmount(inputString)
        }
        break
      }
      case DEPOSIT_TAB:
        setInputAndTxAmount(maxTxValueUnderlyingShaved().toString())
        break
      case WITHDRAW_TAB: {
        setInput(maxTxValueUnderlyingShaved().toString())
        setTxAmount(maxWithdrawNoteUser)
        break
      }
    }
  }

  function setInputAndTxAmount(inputAmount: string) {
    setInput(inputAmount)
    const amount = parseFloat(inputAmount)
    const decimals = broker.underlyingAsset.decimals
    const depNoteDecimals = broker.depositNote.decimals
    const loanNoteDecimals = broker.loanNote.decimals
    const depNoteAmount = scaleUp(amount, depNoteDecimals) / broker.depositNoteExchangeRate
    const loanNoteAmount = scaleUp(amount, loanNoteDecimals) / broker.loanNoteExchangeRate
    const scaledAmount = scaleUp(amount, decimals)

    let txAmount = 0
    switch (selectedTab) {
      case DEPOSIT_TAB:
        txAmount = Math.floor(scaledAmount)
        break
      case WITHDRAW_TAB:
        txAmount = Math.floor(depNoteAmount)
        break
      case REPAY_TAB:
        txAmount = Math.floor(loanNoteAmount)
        break
      case BORROW_TAB:
        txAmount = Math.floor(scaledAmount)
        break
    }
    setTxAmount(txAmount)
  }

  const inputProps = {
    token,
    chain,
    inputVal,
    setInput: setInputAndTxAmount,
    inputDollars,
    setMax,
    hideMax: false,
    focusTrigger
  }

  //OG form component
  return (
    <div className={`tx-form ${isYellowZoneStyles} ${isRedZoneStyles} `}>
      <CloseButton cb={closeForm} />
      <div className="title">
        <div className="is-flex">
          <InfoPoint value={`${selectedTab} ${token.ticker}`} sub={''} />
        </div>
      </div>
      <Tabs tabs={tabs} variant="basic" mods={'full-width'} />
      <InputContainer {...inputProps} />
      <InfoBox {...blueInfoBoxProps} />
      <InfoBox {...orangeInfoBoxProps} />
      <InfoBox {...redInfoBoxProps} />
      <ParamList params={formParams()} more={brokerStats} />
      <button {...actionButtonProps}>{buttonText}</button>
      <ParamList params={belowParams} />
    </div>
  )
}

export default TxForm
